/* eslint-disable no-console */
import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import connect from 'react-redux/es/connect/connect';
import { useLocation } from 'react-router-dom';
import { addQuery, QueryVal, removeQuery } from 'Helpers/Query';
import { QUERY_REFRESH_CURRENT_FOLDER, QUERY_REFRESH_HOME } from 'Consts/File';
import Folder from 'Classes/Folder';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import withEventTrackingLogEvent from 'EventTracking/withEventTrackingLogEvent';
import update from 'immutability-helper';
import HeaderNew from 'Components/ClientApp/CombinedApp/Viewport/SecureFiles/HeaderNew';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import { navigateToPage } from 'Views/Page/Content/Templates';
import useUserContext from 'Context/UserContext';
import {
  FDAlert,
  FDPaper,
} from '@facet-wealth/design';
import RenameModal from './RenameModal';
import FolderModal from './FolderModal';
import Breadcrumb from './Breadcrumb';
import FolderViewer from './FolderViewer';
import FilestoreNavigatorPopover from './FilestoreNavigatorPopover';
import FolderItem from './FolderItem';
import { FileRow } from './FileRow';
import './index.css';


/*
 * Components
 */
const RENAME_MODAL = 'rename';
const FOLDER_MODAL = 'folder';
const NO_MODAL = '';

function SecureFiles({
  user,
  fileRowComponent,
  EventTracking,
  classes,
}) {
  const {
    getFilestoreResource,
    createFolder,
    updateFile,
    deleteFile,
  } = useSwaggerlessContext();
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [curFolder, setCurFolder] = useState(new Folder());
  const [activeModal, setActiveModal] = useState(NO_MODAL);
  const [renameModalFolderItem, setRenameModalFolderItem] = useState({});
  const [uploadProgress, setUploadProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { rootNodeProps } = EventTracking;
  const folderNameQueryVal = QueryVal('folderName');
  const theme = useTheme();
  const { isAgent } = useUserContext();

  useBackgroundColor(theme.palette.facet.lightGray);

  const [
    showStatusChangeConfirmation,
    setShowStatusChangeConfirmation,
  ] = useState(false);
  const [deleteFileItem, setDeleteFileItem] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [fileToMove, setFileToMove] = useState(null);
  const { householdResource } = useSimpleHousehold();
  const householdId = householdResource?.id();

  const shouldShowStatementsNewHomeComponent = !loading && householdResource?.withRelationship(fr.relationships.managedAccountDocumentsPage.name, () => {
    return folderNameQueryVal === 'Facet Account Statements And Confirms';
  }, () => false);

  const handleBreadcrumbs = useCallback((folder, currentBreadCrumbs = breadcrumb) => {
    let newCrumbs = [];

    // check if the breadcrumb contains the folder being navigated to
    const index = currentBreadCrumbs.findIndex(item => item.id === folder.id);
    if (index !== -1) {
      newCrumbs = currentBreadCrumbs.slice(0, index + 1);
    } else if (folder.attributes.name !== '') {
      // add the folder the the breadcrumb
      newCrumbs = [...currentBreadCrumbs, folder].filter((v) => {
        return v.attributes.householdId === householdId;
      });
    }
    return newCrumbs;
  }, [breadcrumb, householdId]);

  const refreshFolder = async (folderId, folderName, onSuccess, onFail) => {
    setLoading(true);
    return getFilestoreResource(householdId, folderId, {})
      .then((res) => {
        const folder = Folder.fromAPIResponse(res, folderId);
        if (!folder) {
          return Promise.resolve();
        }

        folder.attributes.householdId = householdId;
        folder.attributes.name = folderName;

        const updatedBreadCrumb = handleBreadcrumbs(folder, breadcrumb);
        setBreadcrumb(updatedBreadCrumb);
        setCurFolder(folder);

        if (onSuccess) {
          onSuccess();
        }

        return {
          folder,
          updatedBreadcrumb: updatedBreadCrumb,
        };
      })
      .catch((e) => {
        displayErrorMessage(e);
        if (onFail) {
          onFail();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNavigation = useCallback((folderId, onSuccess, onFail, args = {}) => {
    const {
      rootFolder = curFolder,
      breadcrumbOverride,
    } = args;
    if (rootFolder.id === folderId) {
      return Promise.resolve();
    }
    const newFolder = rootFolder.children.find(child => child.id === folderId);

    const folderName = newFolder ? newFolder.attributes.name : 'Home';
    const isFacetAccountStatementsFolder = newFolder ? newFolder.attributes.isFacetAccountStatementsFolder : 'false';
    if (householdId === undefined || householdId === 0 || folderId === 0) {
      return Promise.resolve();
    }

    // This is not a good pattern to follow. Ideally, the current state would be driven off of the self link of the current
    // folder, but swagger is stripping the folder links and meta from the response, so we don't have this readily available
    // This feature is to support tax prep which is currently high priority, so this is a quick hack to get a link to the tax prep
    // folder working
    addQuery({ folderName });

    setLoading(true);
    return getFilestoreResource(householdId, folderId, {})
      .then((res) => {
        const folder = Folder.fromAPIResponse(res, folderId);
        if (!folder) {
          return Promise.resolve();
        }

        folder.attributes.householdId = householdId;
        folder.attributes.name = folderName;
        folder.isFacetAccountStatementsFolder = isFacetAccountStatementsFolder;

        const updatedBreadCrumb = handleBreadcrumbs(folder, breadcrumbOverride);
        setBreadcrumb(updatedBreadCrumb);
        setCurFolder(folder);

        if (onSuccess) {
          onSuccess();
        }
        return {
          folder,
          updatedBreadcrumb: updatedBreadCrumb,
        };
      })
      .catch((e) => {
        displayErrorMessage(e);
        if (onFail) {
          onFail();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [householdId, curFolder]);

  const navigateToFolderName = (folderName) => {
    if (householdId === 0 || !householdResource?.attr('folderId')) {
      return Promise.resolve();
    }

    // For the sake of time, we only support navigating to a folder that is a direct child of the Home folder
    // We could add a recursive search to support multiple levels, but it's just not needed at this time
    // This entire component should be refactored to not use swagger anyway

    return handleNavigation(householdResource?.attr('folderId')).then((res = {}) => {
      const {
        folder: homeFolder,
        updatedBreadcrumb,
      } = res;
      const specifiedFolder = homeFolder.children.find(child => child.attributes.name === folderName);
      if (!specifiedFolder) {
        return Promise.resolve();
      }
      return handleNavigation(specifiedFolder.id, null, null, {
        rootFolder: homeFolder,
        breadcrumbOverride: updatedBreadcrumb,
      });
    });
  };

  useEffect(() => {
    if (householdId === 0 || !householdResource?.attr('folderId')) {
      return;
    }
    if (!folderNameQueryVal || folderNameQueryVal === 'Home') {
      handleNavigation(householdResource?.attr('folderId'));
    } else {
      navigateToFolderName(folderNameQueryVal);
    }
  }, [householdId, householdResource]);

  useEffect(() => {
    //  When location changes there is a CHANCE that we should refresh our content.
    //  We should not always though.
    const shouldRefresh = QueryVal(QUERY_REFRESH_CURRENT_FOLDER);
    if (shouldRefresh) {
      removeQuery(QUERY_REFRESH_CURRENT_FOLDER);
      if (curFolder.id > 0) {
        refreshFolder(curFolder.id, curFolder.attributes.name);
      }
    }
  }, [location]);

  useEffect(() => {
    const shouldRefresh = QueryVal(QUERY_REFRESH_HOME);
    if (shouldRefresh) {
      setBreadcrumb([]);
      removeQuery(QUERY_REFRESH_HOME);
    }
  }, [location]);

  function removeFolderItem(id) {
    const { children } = curFolder;
    const filtered = children.filter(child => child.id !== id);
    setCurFolder({ ...curFolder, children: filtered });
  }

  function handleMoveFile(fromID, toID) {
    const { children } = curFolder;
    const from = children.find(child => child.id === fromID);
    setCurFolder({
      ...curFolder,
      children: children.filter(c => c.id !== from.id),
    });
    updateFile(householdId, from.id, {
      id: from.id,
      type: 'file',
      attributes: { ...from.attributes, parentId: toID },
    })
      .then(() => {
        refreshFolder(curFolder.id, curFolder.attributes.name);
      })
      .catch((e) => {
        displayErrorMessage(e, e.response.body.errors);
        // rerender the old state so the moved file is brought back
        setCurFolder(curFolder);
        setAnchorEl(null);
      });
  }

  function handleUndoFileMove(lastMovedFile) {
    updateFile(householdId, lastMovedFile.id, lastMovedFile)
      .then(() => handleNavigation(curFolder.id))
      .catch((e) => {
        displayErrorMessage(e, 'Failed to undo file move');
      });
  }

  function closeModal() {
    setActiveModal(NO_MODAL);
  }

  function handleDelete(item) {
    setShowStatusChangeConfirmation(true);
    setDeleteFileItem(item);
  }

  function handleFileClick(fileId) {
    const index = curFolder.children.findIndex(item => item.id === fileId);
    const newFolder = update(curFolder, {
      children: {
        [index]: {
          attributes: {
            isViewed: { $set: true },
          },
        },
      },
    });
    setCurFolder(newFolder);
  }


  return (
    <div className={classes.root} test="files-page">
      {loading && <LoadingOverlay />}
      {showStatusChangeConfirmation && (
        <ConfirmationDialog
          message={`This will delete "${deleteFileItem.attributes.name}"`}
          onConfirm={() => {
            setShowStatusChangeConfirmation(false);
            deleteFile(householdId, deleteFileItem.id)
              .then(() => removeFolderItem(deleteFileItem.id))
              .catch((e) => {
                const msg = e.response?.body?.errors ? e.response?.body?.errors : 'Failed to delete file';
                displayErrorMessage(e, msg);
              });
          }}
          onCancel={() => setShowStatusChangeConfirmation(false)}
        />
      )}
      <Box
        id="secure-files"
        paddingBottom={2}
        {...rootNodeProps}
      >
        <HeaderNew
          openFolderModal={() => setActiveModal(FOLDER_MODAL)}
          setUploadProgress={setUploadProgress}
          uploadProgress={uploadProgress}
          handleSuccessfulUpload={() => refreshFolder(Number(curFolder.id), curFolder.attributes.name)}
          folderId={Number(curFolder.id)}
          folderName={curFolder.attributes.name}
          user={user}
          householdId={householdId}
        />
        <FDPaper padding={2}>
          <div className="breadcrumb-container">
            <Breadcrumb items={breadcrumb} handleNavigation={handleNavigation} />
          </div>
          {shouldShowStatementsNewHomeComponent && (
            <FDAlert
              actionLabel={'Take me there'}
              title={'Your account documents have a new home'}
              status={'info'}
              details={["Facet account statements and trade confirmations have moved. Rest assured, nothing has been deleted-they are now located under 'Statements & Tax Docs' in the 'Investments navigation tab."]}
              onClick={() => navigateToPage(householdResource?.relationship(fr.relationships.managedAccountDocumentsPage.name)?.link('related').url(), isAgent)}
            />
          )}
          <FolderViewer
            folder={curFolder}
            handleNavigation={handleNavigation}
            moveFile={handleMoveFile}
            renameFile={updateFile}
            openRenameModal={(item) => {
              setActiveModal(RENAME_MODAL);
              setRenameModalFolderItem(item);
            }}
            onMoveFileClick={(event, file) => {
              setAnchorEl(event.currentTarget);
              setFileToMove(file);
            }}
            removeFolderItem={removeFolderItem}
            handleDelete={handleDelete}
            fileRowComponent={fileRowComponent}
            onFileClick={handleFileClick}
          />
        </FDPaper>
        <FolderModal
          isOpen={activeModal === FOLDER_MODAL}
          closeModal={closeModal}
          createFolder={createFolder}
          folderID={curFolder.id}
          folderName={curFolder.attributes.name}
          refreshFolder={refreshFolder}
          householdId={householdId}
        />
        <RenameModal
          isOpen={activeModal === RENAME_MODAL}
          closeModal={closeModal}
          folderID={Number(curFolder.id)}
          refreshFolder={refreshFolder}
          folderName={curFolder.attributes.name}
          renameModalFolderItem={renameModalFolderItem}
          householdId={householdId}
        />
        <FilestoreNavigatorPopover
          anchorEl={anchorEl}
          moveFile={handleMoveFile}
          undoFileMove={handleUndoFileMove}
          file={fileToMove}
          createFolder={createFolder}
          onClose={() => setAnchorEl(null)}
        />
      </Box>
    </div>
  );
}

/*
 * PropTypes
 */

SecureFiles.propTypes = {
  user: PropTypes.object.isRequired,
  classes: PropTypes.object,
  fileRowComponent: PropTypes.oneOf([
    FolderItem,
    FileRow,
  ]),
};

SecureFiles.defaultProps = {
  fileRowComponent: FolderItem,
  classes: {
    root: '',
  },
};

/*
 * Redux
 */
const mapStateToProps = ({ user }) => ({
  user,
});

/*
 * Exports
 */

const APIConnected = SecureFiles;
const ReduxConnected = connect(mapStateToProps, () => ({}))(APIConnected);
const eventTrackingProperties = {
  nodeName: 'Secure Files',
  isPageView: true,
};
export default withEventTrackingLogEvent(eventTrackingProperties, ReduxConnected);
